/**
 * @file SettingsHubLayout.tsx
 * @description Каркас «Настроек» кабинета магазина: список разделов и
 * выбранный раздел (phase19.31, SC15; замечания владельца 2026-10-02 —
 * «левые блоки меньше, по нажатию справа открывать блок с настройками» и
 * «ширина не везде одинаковая»; page-analysis-c §2 макет (а),
 * uiux-design §5 «Форма и настройки»).
 *
 * - От 1280px: слева колонка разделов 15rem (`SettingsHubColumn`), справа —
 *   страница раздела (`Outlet`) без ухода со страницы «Настройки». Адрес
 *   «Настроек» сразу ведёт в первый доступный раздел — пустой правой части
 *   не бывает.
 * - Ниже 1280px (телефон, планшет, 1024–1279): список разделов — отдельный
 *   экран (`ShopSettingsHubPage`), раздел — отдельный экран со ссылкой
 *   «назад» к списку.
 * - Единая ширина (решение владельца «как в Сводке»): весь макет лежит в
 *   стандартной колонке каркаса (до 1600px по центру); раздел занимает всю
 *   оставшуюся ширину, одинаково у всех разделов и вложенных страниц. Поля
 *   форм раздела — не шире 720px (`[data-settings-section]` в
 *   console-rules.css), карточки — на всю ширину. Режим «на всю ширину»
 *   страницам раздела здесь недоступен (`AppShell.FluidLock`) — край и
 *   ширина не прыгают при переходе между разделами.
 * Адрес в браузере — всегда адрес раздела: прямые ссылки и «назад» работают
 * как раньше. Список разделов — один источник (`visibleSettingsHub`), пункт
 * виден только при наличии права. Нет ни одного доступного раздела (например,
 * логист на странице стадий) — раздел показывается как раньше, без колонки.
 */

import { ChevronLeft } from "lucide-react";
import { Link, Navigate, Outlet, useLocation } from "react-router";

import { useCurrentContext } from "../auth/hooks";
import { SettingsHubColumn } from "../components/shop-nav/SettingsHubColumn";
import { AppShell } from "../components/ui/AppShell";
import { WIDE_QUERY, useMediaQuery } from "../hooks/useMediaQuery";
import { findHubItem, firstHubItem, itemHref, shopRelativePath, visibleSettingsHub } from "../lib/shop-nav";

/** Адрес списка разделов внутри магазина. */
const HUB_PATH = "settings";
/** Контейнер раздела — один на все разделы «Настроек» (вся оставшаяся ширина). */
const SECTION_CLASS = "w-full min-w-0";

export function SettingsHubLayout() {
  const ctx = useCurrentContext();
  const slug = ctx?.shop?.slug ?? "";
  const { pathname } = useLocation();
  const wide = useMediaQuery(WIDE_QUERY);
  const hub = visibleSettingsHub(ctx?.role.permissions ?? []);
  const relativePath = shopRelativePath(pathname, slug);
  const atHub = relativePath === HUB_PATH;

  if (!slug || hub.length === 0) return <Outlet />;

  if (!wide) {
    if (atHub) return <Outlet />;
    // Раздел отдельным экраном — со ссылкой назад к списку разделов.
    return (
      <AppShell.FluidLock>
        <div className={`flex flex-col gap-2 ${SECTION_CLASS}`} data-settings-section="">
          <Link
            to={`/shop/${slug}/${HUB_PATH}`}
            className="-ml-2 inline-flex min-h-12 items-center gap-1 self-start rounded-md px-2 text-sm font-medium text-link outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronLeft aria-hidden className="size-5" />
            Настройки
          </Link>
          <Outlet />
        </div>
      </AppShell.FluidLock>
    );
  }

  const first = firstHubItem(hub);
  if (atHub && first) return <Navigate to={itemHref(slug, first)} replace />;

  const current = findHubItem(hub, relativePath);
  return (
    <AppShell.FluidLock>
      <div
        className="grid grid-cols-[15rem_minmax(0,1fr)] items-start gap-8"
        data-testid="settings-hub-columns"
      >
        <SettingsHubColumn hub={hub} slug={slug} currentId={current?.id ?? null} />
        <div className={SECTION_CLASS} data-testid="settings-section" data-settings-section="">
          <Outlet />
        </div>
      </div>
    </AppShell.FluidLock>
  );
}
